import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 4.2 — organizational context
  const [memberships, setMemberships] = useState([]);
  const [globalRole, setGlobalRole] = useState('user');
  const [activeOrgId, setActiveOrgId] = useState(null);

  // Fetch memberships from server (source of truth)
  const fetchMemberships = useCallback(async () => {
    try {
      const res = await api.get('/auth/me/memberships');
      const data = res.data;
      setGlobalRole(data.global_role || 'user');
      setMemberships(data.memberships || []);
      return data.memberships || [];
    } catch {
      // Network error or 401 — reset to safe defaults
      setMemberships([]);
      setGlobalRole('user');
      return [];
    }
  }, []);

  // Validate and set active org from memberships
  const resolveActiveOrg = useCallback((mbs) => {
    const activeMbs = mbs.filter(m => m.status === 'active');
    // Check if saved preference is still valid
    const savedOrgId = localStorage.getItem('gv_active_org_id');
    const savedIsValid = savedOrgId && activeMbs.some(m => m.org_id === savedOrgId);
    if (savedIsValid) {
      setActiveOrgId(savedOrgId);
    } else if (activeMbs.length > 0) {
      setActiveOrgId(activeMbs[0].org_id);
      localStorage.setItem('gv_active_org_id', activeMbs[0].org_id);
    } else {
      setActiveOrgId(null);
      localStorage.removeItem('gv_active_org_id');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Refresh user + memberships from backend
      api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const freshUser = res.data;
          localStorage.setItem('user', JSON.stringify(freshUser));
          setUser(freshUser);
        })
        .catch(() => {});
      // Fetch memberships (independent call — does not block user display)
      fetchMemberships().then(mbs => resolveActiveOrg(mbs));
    }
    setLoading(false);
  }, [fetchMemberships, resolveActiveOrg]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    
    // Fetch memberships after successful login
    const mbs = await fetchMemberships();
    resolveActiveOrg(mbs);
    
    return user;
  };

  // Auth directe sans appel API — utilisé après vérification email OTP
  const loginDirect = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch memberships
    const mbs = await fetchMemberships();
    resolveActiveOrg(mbs);
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { token, user, requires_validation } = response.data;
    
    // Comptes non validés: pas de token stocké, pas de session
    if (requires_validation || !token) {
      return { user, requires_validation: true };
    }
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    
    // Fetch memberships
    const mbs = await fetchMemberships();
    resolveActiveOrg(mbs);
    
    return { user, requires_validation: false };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('gv_active_org_id');
    setUser(null);
    setMemberships([]);
    setGlobalRole('user');
    setActiveOrgId(null);
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  // Switch active organization — validates against server-provided memberships
  const setActiveOrg = useCallback((orgId) => {
    const valid = memberships.some(m => m.org_id === orgId && m.status === 'active');
    if (valid) {
      setActiveOrgId(orgId);
      localStorage.setItem('gv_active_org_id', orgId);
    }
    // Silently ignore invalid org_id — never grant access to non-member orgs
  }, [memberships]);

  // Derived: active organization object (or null)
  const activeOrg = useMemo(() => {
    if (!activeOrgId) return null;
    return memberships.find(m => m.org_id === activeOrgId && m.status === 'active') || null;
  }, [activeOrgId, memberships]);

  // Permission check helper — server-computed permissions only
  const hasPermission = useCallback((permission) => {
    if (globalRole === 'superadmin') return true;
    if (!activeOrg) return false;
    return activeOrg.permissions.includes(permission);
  }, [globalRole, activeOrg]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginDirect, register, logout, updateUser,
      // Phase 4.2
      memberships, globalRole, activeOrg, setActiveOrg, hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
